import { useState, useEffect, useMemo } from "preact/hooks";
import useCopyToClipboard from "../../hooks/CopyToClipboard";
import useUserContext from "../../contexts/UserContext";
import { mapOfferToPromoBanner, getSecondsUntilEndTime, getVisibleTimerSeconds, formatTime } from "./promoBanner.utils";

export default function usePromoBanner({ onClose, onVisibilityChange }) {
    const [visible, setVisible] = useState(true);
    // Two separate counters: totalTimeLeft drives expiry; timeLeft only counts within the 12 h window
    // and resets when it hits zero (so the display cycles back from MAX_TIMER_SECONDS).
    const [totalTimeLeft, setTotalTimeLeft] = useState(0);
    const [timeLeft, setTimeLeft] = useState(0);
    const [bannerData, setBannerData] = useState(null);

    const { copied, copyToClipboard } = useCopyToClipboard();

    // Extract only `offer` — a stable reference from the underlying generalData state.
    // Depending on the full `{ ...generalData }` spread object would give a new reference
    // on every UserContext re-render, causing the offer-processing effect to loop.
    const [{}, {}, {}, { offer } = {}] = useUserContext();

    // Boolean: offer is undefined until the context fetch resolves (generalData still undefined).
    const isRendered = offer !== undefined && visible && !!bannerData;

    useEffect(() => {
        onVisibilityChange?.(isRendered);
    }, [isRendered, onVisibilityChange]);

    useEffect(() => {
        // offer is undefined while the context fetch is in flight.
        if (offer === undefined) return;

        const mapped = mapOfferToPromoBanner(offer);

        if (!mapped) {
            setVisible(false);
            return;
        }

        const seconds = getSecondsUntilEndTime(mapped.endTime);

        if (seconds <= 0) {
            setVisible(false);
            return;
        }

        setTotalTimeLeft(seconds);
        setTimeLeft(getVisibleTimerSeconds(seconds));
        setBannerData(mapped);
    }, [offer]);

    useEffect(() => {
        if (!visible || !bannerData) return;

        const interval = setInterval(() => {
            // setTimeLeft is nested inside setTotalTimeLeft's updater so it always
            // reads the freshly computed nextTotal — avoids a stale-closure mismatch.
            setTotalTimeLeft((prevTotal) => {
                const nextTotal = prevTotal - 1;

                if (nextTotal <= 0) {
                    clearInterval(interval);
                    setTimeLeft(0);
                    setVisible(false);
                    return 0;
                }

                setTimeLeft((prev) => {
                    const next = prev - 1;
                    return next <= 0 ? getVisibleTimerSeconds(nextTotal) : next;
                });

                return nextTotal;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [visible, bannerData]);

    const handleClose = () => {
        setVisible(false);
        onClose?.();
    };

    const handleCopy = () => {
        if (bannerData?.coupon) copyToClipboard(bannerData.coupon);
    };

    const time = useMemo(() => formatTime(timeLeft), [timeLeft]);

    return {
        time,
        handleClose,
        handleCopy,
        copied,
        bannerData,
        isRendered,
        hasPrice: bannerData?.price != null,
        hasSpecialPrice: bannerData?.specialPrice != null,
    };
}
